import { AXIOS_CONFIG } from '@/lib/Apis/wrapper';
import { ProductsApi, Product, PersonalizedProductsQueryDtoSortEnum } from '@/lib/Apis';

import { useQuery } from '@tanstack/react-query';
import { getDeviceId } from '@/lib/utils/deviceId';
import { getPersonalizationSessionStartedAt } from '@/lib/utils/personalizationSession';
import { ProductFilters } from '@/lib/types';

interface UsePersonalizedProductsOptions {
    /** Product filters (category, subcategory, price range, etc.) */
    filters?: ProductFilters;
    /** Number of products to return */
    limit?: number;
    /** Current page for pagination */
    page?: number;
    /** Percentage of personalized products (0-100, default 40) */
    personalizedRatio?: number;
    /** Place admin-selected featured products before personalized and regular products */
    featuredFirst?: boolean;
    /** Backend sort order */
    sort?: PersonalizedProductsQueryDtoSortEnum;
    /** Whether to enable the query */
    enabled?: boolean;
}

/**
 * Hook to fetch personalized product recommendations based on user browsing history.
 * Uses device ID for anonymous users and auth token for logged-in users.
 * Supports the same filters as useProducts (category, subcategory, price range).
 * 
 * @param options - Configuration options for the query
 * @returns Personalized products response with loading/error states
 */
export function usePersonalizedProducts(options: UsePersonalizedProductsOptions = {}) {
    const {
        filters,
        limit = 20,
        page = 1,
        personalizedRatio = 40,
        featuredFirst = false,
        sort,
        enabled = true
    } = options;

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: [
            'personalizedProducts',
            filters?.category,
            filters?.categories,
            filters?.subCategory,
            filters?.maxPrice,
            filters?.minPrice,
            filters?.inStock,
            filters?.onSale,
            filters?.brand,
            filters?.brands,
            limit,
            page,
            personalizedRatio,
            featuredFirst,
            sort,
        ],
        queryFn: async () => {
            const deviceId = getDeviceId();
            const personalizationSessionStartedAt = getPersonalizationSessionStartedAt();
            const selectedCategories = filters?.categories?.length
                ? filters.categories
                : filters?.category
                    ? [filters.category]
                    : [];
            const selectedBrands = filters?.brands?.length
                ? filters.brands
                : filters?.brand
                    ? [filters.brand]
                    : [];

            const response = await new ProductsApi(AXIOS_CONFIG).getPersonalizedProducts(
                {
                    page,
                    limit,
                    personalizedRatio,
                    featuredFirst,
                    personalizationSessionStartedAt,
                    sort,
                    brandFilter: selectedBrands.join(',') || undefined,
                    filters: {
                        category: selectedCategories.length === 1 ? selectedCategories[0] : undefined,
                        categories: selectedCategories.length ? selectedCategories : undefined,
                        brands: selectedBrands.length ? selectedBrands : undefined,
                        subCategory: filters?.subCategory,
                        minPrice: filters?.minPrice,
                        maxPrice: filters?.maxPrice,
                        inStock: filters?.inStock,
                        onSale: filters?.onSale,
                    },
                },
                deviceId,
            );

            return response.data;
        },
        staleTime: 2 * 60 * 1000, // 2 minutes - personalized results can be cached briefly
        enabled,
    });

    return {
        products: (data?.products as unknown as Product[]) || [],
        isPersonalized: data?.isPersonalized ?? false,
        personalizedCount: data?.personalizedCount ?? 0,
        regularCount: data?.regularCount ?? 0,
        availableBrands: data?.availableBrands ?? [],
        isLoading,
        error: error as Error | null,
        refetch,
        pagination: {
            page: data?.page ?? page,
            limit,
            total: data?.total ?? 0,
            totalPages: data?.totalPages ?? 0,
        },
    };
}
